// Shared atoms + styles

import React from "react";
import { I } from "./icons.jsx";
import "./atoms.css";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const isVisibleFocusable = (node) => {
  if (!(node instanceof HTMLElement)) return false;
  if (node.closest("[aria-hidden='true']")) return false;
  return node.offsetParent !== null || node === document.activeElement;
};

const getDialogFocusables = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisibleFocusable);

const dialogStack = [];
const isTopDialog = (root) => dialogStack[dialogStack.length - 1] === root;
let hadKeyboardInteraction = false;
let modalityListenersInstalled = false;

const installInteractionModalityListeners = () => {
  if (modalityListenersInstalled || typeof window === "undefined") return;
  modalityListenersInstalled = true;
  window.addEventListener("keydown", (event) => {
    if (event.key === "Tab" || event.key === "Enter" || event.key === " ") {
      hadKeyboardInteraction = true;
    }
  }, true);
  const markPointer = () => {
    hadKeyboardInteraction = false;
  };
  window.addEventListener("pointerdown", markPointer, true);
  window.addEventListener("mousedown", markPointer, true);
};
installInteractionModalityListeners();

const focusFirstDialogTarget = (root) => {
  const nodes = getDialogFocusables(root);
  const target = nodes[0] || root;
  target.focus({ preventScroll: true });
};

const useDialogFocus = ({ enabled = true, onClose, restoreFocusTo } = {}) => {
  const dialogRef = React.useRef(null);
  React.useEffect(() => {
    if (!enabled) return undefined;
    const root = dialogRef.current;
    if (!root) return undefined;
    installInteractionModalityListeners();
    dialogStack.push(root);
    const previousFocus = restoreFocusTo instanceof HTMLElement
      ? restoreFocusTo
      : document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusables = getDialogFocusables(root);
    const firstTarget = hadKeyboardInteraction ? focusables[0] || root : root;
    const focusTimer = window.setTimeout(() => {
      if (!root.isConnected) return;
      firstTarget.focus({ preventScroll: true });
    }, 0);

    const onKeyDown = (event) => {
      if (!isTopDialog(root)) return;
      if (event.key === "Escape" && onClose) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = getDialogFocusables(root);
      if (!nodes.length) {
        event.preventDefault();
        root.focus({ preventScroll: true });
        return;
      }
      if (!root.contains(document.activeElement)) {
        event.preventDefault();
        focusFirstDialogTarget(root);
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const onFocusIn = (event) => {
      if (!isTopDialog(root)) return;
      if (!root.isConnected) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      focusFirstDialogTarget(root);
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
      const stackIndex = dialogStack.lastIndexOf(root);
      if (stackIndex !== -1) dialogStack.splice(stackIndex, 1);
      if (previousFocus && previousFocus.isConnected) {
        window.setTimeout(() => {
          if (previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
        }, 0);
      }
    };
  }, [enabled, onClose, restoreFocusTo]);
  return dialogRef;
};

// components
const Toggle = ({ on, onChange, variant="", ariaLabel = "Toggle setting" }) => {
  const toggle = (event) => {
    event.stopPropagation();
    onChange?.(!on);
  };
  return (
    <div
      className={`toggle ${variant} ${on ? "on" : ""}`}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle(event);
        }
      }}
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      tabIndex={0}
    />
  );
};

const CHIP_TONES = new Set(["gold", "hot"]);

const Chip = ({ children, onRemove, tone }) => {
  const namedTone = CHIP_TONES.has(tone);
  return (
    <span
      className={`chip${namedTone ? ` ${tone}` : ""}`}
      style={tone && !namedTone ? { background: tone, borderColor: "transparent" } : null}
    >
      {children}
      {onRemove ? <button className="x-btn" onClick={onRemove} aria-label="remove"><I.x size={10}/></button> : null}
    </span>
  );
};

const Badge = ({ children, tone="neutral", icon, title, ariaLabel, testId, className = "" }) => {
  const hasText = React.Children.toArray(children).some(child => child != null && child !== false && String(child).trim() !== "");
  if (!icon && !hasText) return null;
  return (
    <span
      className={`badge ${tone}${className ? ` ${className}` : ""}`}
      title={title}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {icon ? <span style={{display:"inline-flex", marginLeft:-1}}>{icon}</span> : null}
      {children}
    </span>
  );
};

const VALUE_TIER_ASSETS = {
  S: "assets/value-tier-s.png",
  A: "assets/value-tier-a.png",
  B: "assets/value-tier-b.png",
  C: "assets/value-tier-c.png",
  D: "assets/value-tier-d.png",
  F: "assets/value-tier-f.png",
};

const valueTierAssetSrc = (tier) => VALUE_TIER_ASSETS[tier] || null;

const ValueTierGlyph = ({ tier, size = "display", ariaLabel, testId = "value-tier-glyph" }) => {
  const assetSrc = valueTierAssetSrc(tier);
  if (!assetSrc) return null;
  const accessibility = ariaLabel
    ? { role: "img", "aria-label": ariaLabel, title: ariaLabel }
    : { "aria-hidden": "true" };
  return (
    <span
      className={`value-tier-glyph ${size}`}
      data-testid={testId || undefined}
      data-tier={tier}
      {...accessibility}
    >
      <img src={assetSrc} alt="" aria-hidden="true" decoding="async" />
    </span>
  );
};

const ValueTierDisplay = ({ tier, variant = "" }) => {
  const label = tier ? `${tier} tier` : "Not tiered";
  if (valueTierAssetSrc(tier)) {
    const size = variant === "hero" ? "hero" : "display";
    return (
      <span
        className={`value-tier-display glyph-tier ${tier.toLowerCase()}-tier ${variant}`}
        data-testid="value-tier-display"
        data-tier={tier}
        role="img"
        aria-label={label}
        title={label}
      >
        <ValueTierGlyph tier={tier} size={size} testId={null} />
      </span>
    );
  }
  return (
    <span className={`value-tier-display ${variant}`} data-testid="value-tier-display" data-tier={tier || ""}>
      {tier ? label : "—"}
    </span>
  );
};

const Segmented = ({ value, options, onChange }) => (
  <div className="seg">
    {options.map(o => (
      <button key={o.value} className={value === o.value ? "active" : ""} onClick={() => onChange(o.value)}>
        {o.icon}{o.label}
      </button>
    ))}
  </div>
);

const RangeInput = ({ minLabel, maxLabel, unit }) => (
  <div className="range-row">
    <input className="input mono" placeholder={minLabel} />
    <span className="dash">–</span>
    <input className="input mono" placeholder={maxLabel} />
  </div>
);

const Tooltip = ({ children, label }) => (
  <span className="tt-wrap" title={label} style={{position:"relative", display:"inline-flex"}}>
    {children}
  </span>
);

const ComingSoon = ({
  open = true,
  variant = "modal",
  feature = "Coming soon",
  detail = "This account feature is paused for the public MVP while the live deal dashboard stays open.",
  onClose,
  onAction,
  actionLabel,
}) => {
  const titleId = React.useId();
  const detailId = React.useId();
  const isModal = variant === "modal";
  const TitleTag = isModal ? "h2" : "h3";
  const dialogRef = useDialogFocus({ enabled: open && isModal, onClose });

  if (!open) return null;
  const body = (
    <div
      className={`coming-soon ${variant}`}
      data-testid={`coming-soon-${variant}`}
      ref={isModal ? dialogRef : undefined}
      role={isModal ? "dialog" : undefined}
      aria-modal={isModal ? "true" : undefined}
      aria-labelledby={isModal ? titleId : undefined}
      aria-describedby={isModal ? detailId : undefined}
      tabIndex={isModal ? -1 : undefined}
    >
      <div className="cs-top">
        <div style={{display:"flex", gap:12, minWidth:0}}>
          <span className="cs-icon"><I.spark size={16}/></span>
          <div style={{minWidth:0}}>
            <Badge tone="outline">Coming soon</Badge>
            <TitleTag id={titleId} data-testid="coming-soon-title">{feature}</TitleTag>
            <p id={detailId}>{detail}</p>
          </div>
        </div>
        {variant === "modal" && onClose ? (
          <button className="btn icon sm ghost" type="button" onClick={onClose} aria-label="Close"><I.x size={14}/></button>
        ) : null}
      </div>
      {(onAction || variant === "modal") && (
        <div className="cs-actions">
          <button className="btn accent sm" type="button" onClick={onAction || onClose}>
            {actionLabel || (variant === "modal" ? "Got it" : "Learn more")}
          </button>
        </div>
      )}
    </div>
  );
  if (variant !== "modal") return body;
  return (
    <>
      <div className="soon-scrim" onClick={onClose}/>
      {body}
    </>
  );
};

export {
  Badge,
  Chip,
  ComingSoon,
  RangeInput,
  Segmented,
  Toggle,
  Tooltip,
  ValueTierDisplay,
  ValueTierGlyph,
  valueTierAssetSrc,
  useDialogFocus,
};
